import type { MaybeRefOrGetter } from 'vue'
import type { PageHeaderActionItem } from '~/composables/usePageHeaderActions'

export function useScopedPageActions(actions: MaybeRefOrGetter<PageHeaderActionItem[]>) {
  const pageHeaderActions = usePageHeaderActions()
  const mobilePageMenu = useMobilePageMenu()
  let ownedActions: PageHeaderActionItem[] | null = null
  let ownedMobileMenu: ReturnType<typeof useMobilePageMenu>['value'] | null = null

  watchEffect(() => {
    const resolvedActions = toValue(actions)
    const usesStructuredActions = resolvedActions.some(item => item.primary || item.section)
    const primaryActions = usesStructuredActions
      ? resolvedActions.filter(item => item.primary)
      : resolvedActions
    const secondaryActions = usesStructuredActions
      ? resolvedActions.filter(item => !item.primary)
      : []
    const groupedSections = new Map<string, typeof secondaryActions>()

    for (const item of secondaryActions) {
      const label = item.section?.trim() || 'Actions'
      const items = groupedSections.get(label) || []
      items.push(item)
      groupedSections.set(label, items)
    }

    const resolvedMobileMenu = {
      sections: Array.from(groupedSections.entries())
        .sort(([a], [b]) => {
          const order = ['Actions', 'Guides']
          const aIndex = order.indexOf(a)
          const bIndex = order.indexOf(b)
          if (aIndex === -1 && bIndex === -1) return 0
          if (aIndex === -1) return 1
          if (bIndex === -1) return -1
          return aIndex - bIndex
        })
        .map(([label, items]) => ({
          id: label.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          label,
          items: items.map(({ id, label: itemLabel, icon, action }) => ({
            id,
            label: itemLabel,
            icon,
            action,
          })),
        })),
      actions: primaryActions.map(({ id, label, icon, action }) => ({
        id,
        label,
        icon,
        action,
      })),
    }
    ownedActions = resolvedActions
    ownedMobileMenu = resolvedMobileMenu
    pageHeaderActions.value = resolvedActions
    mobilePageMenu.value = resolvedMobileMenu
  })

  onBeforeUnmount(() => {
    if (pageHeaderActions.value === ownedActions) resetPageHeaderActions()
    if (mobilePageMenu.value === ownedMobileMenu) resetMobilePageMenu()
  })
}
